import { getLinkableLocality, normalizeLocality } from 'utils/trainer-locality';
import Link from 'next/link';
import { stripSensitiveFields } from 'utils/strip-sensitive-fields';
import React from 'react'
import Head from 'next/head'
import { Profile } from '../../../components/profile/Profile'
import { useRouter } from 'next/router'
import { downloadAbout, downloadAllUserId, downloadCarousel, downloadProfilePic, getStringUserIdFromHandleName } from '../../../api/user/profile-api'
import Script from 'next/script'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps({params}) {
  // Fetch data from external API
  
  const handleName = params.handleName
  
  // Check if handleName is actually a 10-digit stringsUserId
  if (/^\d{10}$/.test(handleName)) {
    // It's a stringsUserId, fetch user details to get actual handleName
    const about = stripSensitiveFields(await downloadAbout("", handleName))
    
    // Redirect to the correct profile URL with handleName
    return {
      redirect: {
        destination: `/profile/${about.handleName}`,
        permanent: true, // 301 redirect
      },
    }
  }
  
  // Continue with existing logic for normal handleName
  const stringsUserId = (await getStringUserIdFromHandleName(handleName)).toString()
  const about = stripSensitiveFields(await downloadAbout("", stringsUserId))
  const profilePicture = await downloadProfilePic(stringsUserId)
  const timeline = await downloadCarousel(stringsUserId)

  return {
    props: {
      handleName,
      stringsUserId,
      about,
      profilePicture,
      timeline
    },
    // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
    revalidate: 60, // In seconds
  }
}
// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {

    const users = await downloadAllUserId()
    // Get the paths we want to pre-render based on posts
    const paths = users.map((user) => ({
      params: { handleName: user.handleName },
    }))

    // We'll pre-render only these paths at build time.
    // { fallback: blocking } will server-render pages
    // on-demand if the path doesn't exist.
    return { paths, fallback: 'blocking' }
}

export default function Home(props) {
  const router = useRouter()
  const stringHandleName = props.handleName
  const stringsUserId = props.stringsUserId

  const about = props.about
  const profilePicture = props.profilePicture
  const timeline = props.timeline

  // `about` can come back empty if the provider record is missing, and these
  // strings end up in the <title> and in JSON-LD. Without fallbacks the page
  // renders "undefined undefined undefined" and gets indexed that way.
  const fullName = [about.userFirstName, about.userLastName]
      .filter(Boolean).join(" ").trim()

  let description = fullName
      ? `${fullName} StringsSG Profile.`
      : "StringsSG service provider profile."
  if(about.description > ""){
      description = about.description
  }

  const category = () => {
    switch (about.category) {
      case "Fitness Trainer":
        return "Personal Trainer"
      default:
        return about.category
    }
  }

  const profileUrl = `https://www.stringssg.com/profile/${stringHandleName}`
  const isTrainer = about.category === "Fitness Trainer"
  // Stored userLocation is free text and often a bare postal code or a full
  // street address. See utils/trainer-locality.
  const locality = normalizeLocality(about.userLocation)
  // normalizeLocality is display-only and recognises areas with no page of their
  // own (Novena, Punggol, Katong...). The trainer-list route is fallback:false,
  // so link through the nearest locality that actually has a page.
  const linkLocality = getLinkableLocality(locality)
  // Trainer profiles lead with the locality: "Personal Trainer in Bedok" is the
  // shape people actually search, and it differentiates ~190 otherwise similar
  // profile titles from each other.
  const displayName = fullName || "StringsSG Member"
  const pageTitle = isTrainer
    ? `${displayName} - Personal Trainer in ${locality}, Singapore | StringsSG`
    : `${displayName}${category() ? " " + category() : ""} | StringsSG${about.countryName ? " - " + about.countryName : ""}`
  const socialTitle = isTrainer
    ? `${displayName} - Personal Trainer in ${locality}, Singapore`
    : `${displayName}${about.category ? " | " + about.category : ""} | StringsSG`

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content={description}/>
        <meta name="keywords" content="House Rental, Insurance Plan, Personal Trainer, Maid Service, Handyman Service, Aircon Service, Savings Plan, Food Catering, Interior Design, Wedding Planner, Event Planner"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content={"https://www.stringssg.com/profile/" + stringHandleName} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={socialTitle} />
        <meta property="og:description" content={description}/>
        <meta property="og:image"  content={profilePicture} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={profilePicture} />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={"https://www.stringssg.com/profile/" + stringHandleName}/>
        <meta name="twitter:title" content={socialTitle}/> 
        <meta name="twitter:description" content={description}/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href={"https://www.stringssg.com/profile/" + stringHandleName} />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Person",
                // `sameAs` used to point at this same page, which tells a crawler
                // nothing. Dropped rather than replaced - we have no verified
                // external profiles to cite.
                "name": displayName,
                "url": profileUrl,
                "image": profilePicture,
                "jobTitle": category(),
                "description": description,
                ...(isTrainer ? {
                  "address": {
                    "@type": "PostalAddress",
                    "addressLocality": locality,
                    "addressCountry": "SG"
                  },
                  "areaServed": { "@type": "Place", "name": locality },
                  "knowsAbout": ["Personal Training", "Fitness Coaching"]
                } : {}),
                "worksFor": {
                  "@type": "Organization", "@id": "https://www.stringssg.com/#organization",
                  "name": "StringsSG",
                  "url": "https://www.stringssg.com/"
                }
              },
              {
                // Was a single self-referential item, which is not a trail.
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.stringssg.com/" },
                  ...(isTrainer ? [{
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Personal Trainer Singapore",
                    "item": "https://www.stringssg.com/sg/personal-trainer"
                  }] : []),
                  { "@type": "ListItem", "position": isTrainer ? 3 : 2, "name": displayName, "item": profileUrl }
                ]
              }
            ]
          }) }}
        />
      </Head>  
        <div className="max-w-4xl mx-auto overflow-hidden"> 
            <Profile about={about} stringsUserId={stringsUserId} stringHandleName={stringHandleName} profilePicture={profilePicture} timeline={timeline}/>
            {/* Profiles used to dead-end. These feed the hub and the locality
                page, and give a visitor who landed here from search somewhere
                obvious to go next. */}
            {isTrainer &&
                <nav className="px-4 py-6 text-sm text-gray-500 border-t border-gray-100 mt-6">
                    <span>More personal trainers: </span>
                    {/* Skipped when it resolves island-wide: the hub link below
                        already says exactly that, and two near-identical links
                        help nobody. */}
                    {linkLocality !== "Singapore" &&
                        <>
                            <Link href={`/sg/personal-trainer/trainer-list/${encodeURIComponent(linkLocality)}`} prefetch={false} className="text-orange-600 hover:underline">
                                personal trainers in {linkLocality}
                            </Link>
                            <span> &middot; </span>
                        </>
                    }
                    <Link href="/sg/personal-trainer" prefetch={false} className="text-orange-600 hover:underline">
                        certified personal trainers in Singapore
                    </Link>
                </nav>
            }
        </div>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
